import { Editor as EditorClass } from '@tiptap/core';
import capitalize from 'lodash/capitalize';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useLatest } from 'react-use';

import { CopyRichTextButton } from '@eluve/blocks';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Skeleton,
  cn,
  legacyTextStyles,
} from '@eluve/components';
import { EluveEditor, EluveEditorHandle } from '@eluve/eluve-editor';
import { getTiptapMarkdownContentFromEditor } from '@eluve/markdown-utils';
import { useAnalytics } from '@eluve/posthog-react';
import { areStringsEqualWithoutSpecialCharacters } from '@eluve/utils';
import { AppointmentSummaryKeys } from '@eluve/utils';

export type SummarySectionMarkdownAction = React.FC<{
  getSummary: () => string | null | undefined;
}>;

export interface SummarySectionMarkdownProps {
  className?: string;
  summaryKey: AppointmentSummaryKeys;
  sectionTitle: string;
  content?: string;
  loading?: boolean;
  handleContentChange: (
    key: AppointmentSummaryKeys,
    newContent: string,
  ) => void;
  disabled: boolean;
  additionalActions?: SummarySectionMarkdownAction[];
}

export const SummarySectionMarkdown: React.FC<SummarySectionMarkdownProps> = ({
  className,
  summaryKey,
  sectionTitle: title,
  content,
  loading,
  handleContentChange,
  disabled,
  additionalActions,
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<EluveEditorHandle | null>(null);
  const latestContent = useLatest(content);
  const analytics = useAnalytics('appointment-summary-section');

  // TODO(jesse)[ELU-981]: This should be debounced so we don't spam
  // updates across the tree with every keystroke
  const onEditorContentUpdated = (
    editor?: EditorClass | undefined,
  ): void | Promise<void> => {
    if (editor) {
      if (
        areStringsEqualWithoutSpecialCharacters(
          latestContent.current,
          getTiptapMarkdownContentFromEditor(editor),
        )
      ) {
        return;
      }
      const content = getTiptapMarkdownContentFromEditor(editor);
      handleContentChange(summaryKey, content);
    }
  };

  useEffect(() => {
    editorRef?.current?.setEditable(!loading && !disabled);
  }, [editorRef, disabled, loading]);

  const showEditor = Boolean(!loading || (loading && content));

  return (
    // eslint-disable-next-line tailwindcss/no-custom-classname
    <div className={cn('viewer [&_div[id^=tippy-]]:!z-20', className)}>
      <Card className="w-full rounded-xl border-border-primary">
        <CardHeader className="flex h-10 flex-row place-items-center justify-between rounded-t-xl border-b bg-bg-primary p-0 px-5 tracking-normal">
          <Box hStack className="gap-2">
            <h3
              className={legacyTextStyles.body({
                size: 'm',
                weight: 'semibold',
              })}
            >
              <b className="font-semibold">
                {title
                  .split('_')
                  .map((s) => capitalize(s))
                  .join(' ')}
              </b>
            </h3>
            {!loading && Boolean(content?.length) && (
              <>
                <CopyRichTextButton
                  getContentElement={() => contentRef.current}
                  hideContentInTooltip
                  markdownContent={content}
                  onCopy={() => {
                    analytics.capture('appointment.note_section_copied', {
                      section_name: summaryKey,
                    });
                  }}
                />
                {additionalActions?.map((Action, i) => (
                  <Action key={i} getSummary={() => latestContent.current} />
                ))}
              </>
            )}
          </Box>
          {loading && (
            <Loader2 className="h-5 animate-spin text-border-accent-quaternary" />
          )}
        </CardHeader>
        <hr className="sr-only" />
        <CardContent
          className="flex flex-col justify-between gap-5 rounded-b-xl bg-white p-4"
          ref={contentRef}
        >
          {loading && !content && (
            <>
              <Skeleton className="h-3" />
              <Skeleton className="h-3" />
            </>
          )}

          {showEditor && (
            <EluveEditor
              ref={editorRef}
              content={content}
              onUpdate={onEditorContentUpdated}
            />
          )}
        </CardContent>
      </Card>
      <br className="sr-only" />
    </div>
  );
};
